interface TrialCandidate {
	name?: string | null;
	expiresAt?: string | null;
}

interface BuildPlanLabelParams {
	language?: string | null;
	planLabel?: string | null;
	legacyPlanName?: string | null;
	legacyTrialName?: string | null;
	legacyTrialExpiresAt?: string | null;
	subscriptionStatus?: string | null;
	subscriptionPlanName?: string | null;
	subscriptionExpiresAt?: string | null;
	now?: Date;
}

const parseCalendarDate = (value?: string | null): Date | null => {
	if (typeof value !== "string" || value.trim() === "") {
		return null;
	}

	const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (calendarDate) {
		const [, year, month, day] = calendarDate;
		const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

		if (
			parsedDate.getFullYear() === Number(year) &&
			parsedDate.getMonth() === Number(month) - 1 &&
			parsedDate.getDate() === Number(day)
		) {
			return parsedDate;
		}

		return null;
	}

	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) {
		return null;
	}

	return new Date(
		parsedDate.getFullYear(),
		parsedDate.getMonth(),
		parsedDate.getDate()
	);
};

const getValidTrial = (
	trial: TrialCandidate,
	now: Date
): { name: string; expiresAt: Date } | null => {
	const name = trial.name?.trim();
	const expiresAt = parseCalendarDate(trial.expiresAt);
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	if (!name || !expiresAt || expiresAt.getTime() < today.getTime()) {
		return null;
	}

	return { name, expiresAt };
};

const formatDate = (date: Date) =>
	`${String(date.getDate()).padStart(2, "0")}/${String(
		date.getMonth() + 1
	).padStart(2, "0")}/${date.getFullYear()}`;

export const buildPlanLabel = ({
	language,
	planLabel,
	legacyPlanName,
	legacyTrialName,
	legacyTrialExpiresAt,
	subscriptionStatus,
	subscriptionPlanName,
	subscriptionExpiresAt,
	now = new Date(),
}: BuildPlanLabelParams): string => {
	const subscriptionTrial =
		subscriptionStatus === "trial"
			? getValidTrial(
					{
						name: subscriptionPlanName,
						expiresAt: subscriptionExpiresAt,
					},
					now
			  )
			: null;
	const legacyTrial = getValidTrial(
		{
			name: legacyTrialName,
			expiresAt: legacyTrialExpiresAt,
		},
		now
	);
	const trial = subscriptionTrial || legacyTrial;
	const normalizedPlanLabel = planLabel?.trim() || "";

	if (trial) {
		return language === "Português"
			? `${normalizedPlanLabel} Trial ${trial.name} ${formatDate(
					trial.expiresAt
			  )}`.trim()
			: `${trial.name} ${normalizedPlanLabel}`.trim();
	}

	const planName = legacyPlanName?.trim() || subscriptionPlanName?.trim() || "";

	return language === "Português"
		? `${normalizedPlanLabel} ${planName}`.trim()
		: `${planName} ${normalizedPlanLabel}`.trim();
};
